import React from "react";

/**
 * PowerBuilt icon mark — stylized house with a sun.
 * Sized via className (defaults to 32×32). Colors respect Navy #0A2540 + Gold #C9A356.
 * The `variant` prop switches between:
 *   - "mark": icon only (default, for sidebars / favicons)
 *   - "full": icon + wordmark to its right (for headers)
 */
export function PowerBuiltMark({ className = "w-9 h-9", variant = "mark", darkOnLight = false }) {
  // On the navy sidebar we want the house strokes light and the sun gold.
  // On light backgrounds we invert: house navy, sun gold.
  const houseColor = darkOnLight ? "#0A2540" : "#FFFFFF";
  const roofColor  = "#C9A356";
  const sunColor   = "#C9A356";
  const wallStroke = darkOnLight ? "#0A2540" : "#FFFFFF";

  const mark = (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      {/* Sun — top-right, arc + rays */}
      <circle cx="34" cy="12" r="5" fill={sunColor} />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
        const rad = (deg * Math.PI) / 180;
        const x1 = 34 + Math.cos(rad) * 7.5;
        const y1 = 12 + Math.sin(rad) * 7.5;
        const x2 = 34 + Math.cos(rad) * 10.5;
        const y2 = 12 + Math.sin(rad) * 10.5;
        return (
          <line
            key={deg}
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={sunColor} strokeWidth="1.6" strokeLinecap="round"
            opacity={deg >= 135 && deg <= 225 ? 0.35 : 0.9}
          />
        );
      })}

      {/* House body — pitched roof + walls */}
      <path
        d="M8 26 L24 14 L40 26"
        stroke={roofColor}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M11 25 V38 H37 V25"
        stroke={wallStroke}
        strokeWidth="2.2"
        strokeLinejoin="round"
        fill={houseColor}
        fillOpacity={darkOnLight ? 0.04 : 0.06}
      />
      {/* Door */}
      <rect x="21" y="30" width="6" height="8" rx="0.6" fill={roofColor} />
      {/* Solar-panel stripe on the roof — signals renewable focus */}
      <path
        d="M17 22.3 L27.5 14.5"
        stroke="#5B8DEF"
        strokeWidth="2.4"
        strokeLinecap="round"
        opacity="0.85"
      />
    </svg>
  );

  if (variant === "mark") return mark;
  return (
    <span className="inline-flex items-center gap-2.5">
      {mark}
      <span className="flex flex-col leading-none">
        <span className="text-sm font-bold tracking-wider" style={{ color: darkOnLight ? "#0A2540" : "#FFFFFF" }}>
          POWERBUILT
        </span>
      </span>
    </span>
  );
}
